import { useState, useMemo } from 'react';

/**
 * Department scope context banner for custodian / coordinator / technician dashboards.
 *
 * States:
 * - 1 department — compact status (inventory / personal assignment when relevant)
 * - 2+ departments — expandable multi-department categorization
 *
 * Department-scoped roles always have at least one department assigned.
 *
 * Two distinct signals (when provided):
 * (a) noUserAssignmentNames — departments where the user has no personal device assignment
 * (b) emptyInventoryNames — departments that have zero devices in inventory
 *
 * @param {Array<{id?: string|number, name: string, icon?: string}>} departments
 * @param {string[]} [noUserAssignmentNames]
 * @param {string[]} [emptyInventoryNames]
 * @param {boolean} [showUserAssignment=true] — false for coordinator/custodian (dept inventory or custody-scoped lists)
 */
const DepartmentAssignmentBanner = ({
  departments = [],
  noUserAssignmentNames = [],
  emptyInventoryNames = [],
  showUserAssignment = true,
}) => {
  const [expanded, setExpanded] = useState(false);

  const noAssignmentSet = useMemo(() => new Set(noUserAssignmentNames), [noUserAssignmentNames]);
  const emptyInventorySet = useMemo(() => new Set(emptyInventoryNames), [emptyInventoryNames]);

  const groups = useMemo(() => {
    const assignedToYou = [];
    const notAssignedToYou = [];
    const emptyDepartments = [];
    const withInventory = [];

    departments.forEach((dept) => {
      const isEmptyInventory = emptyInventorySet.has(dept.name);
      if (isEmptyInventory) {
        emptyDepartments.push(dept);
        return;
      }
      if (!showUserAssignment) {
        withInventory.push(dept);
        return;
      }
      if (noAssignmentSet.has(dept.name)) {
        notAssignedToYou.push(dept);
      } else {
        assignedToYou.push(dept);
      }
    });

    return { assignedToYou, notAssignedToYou, emptyDepartments, withInventory };
  }, [departments, noAssignmentSet, emptyInventorySet, showUserAssignment]);

  // Department-scoped roles always have ≥1 department; guard only.
  if (!departments || departments.length === 0) {
    return null;
  }

  const renderDeptChip = (dept, variant = '') => (
    <li
      key={dept.id || dept.name}
      className={`dept-assignment-banner__chip${variant ? ` dept-assignment-banner__chip--${variant}` : ''}`}
    >
      <span className="dept-assignment-banner__chip-icon" aria-hidden="true">
        {dept.icon || '🏢'}
      </span>
      <span className="dept-assignment-banner__chip-name">{dept.name}</span>
    </li>
  );

  const renderGroup = (label, items, variant = '') => {
    if (!items.length) return null;
    return (
      <div className="dept-assignment-banner__group">
        <div className="dept-assignment-banner__group-label">
          {label} ({items.length})
        </div>
        <ul className={`dept-assignment-banner__chips${variant ? ` dept-assignment-banner__chips--${variant}` : ''}`}>
          {items.map((dept) => renderDeptChip(dept, variant))}
        </ul>
      </div>
    );
  };

  // --- 1 department: compact status ---
  if (departments.length === 1) {
    const dept = departments[0];
    const isEmptyInventory = emptyInventorySet.has(dept.name);
    const isNoAssignment = showUserAssignment && noAssignmentSet.has(dept.name);

    let note = null;
    let variantClass = '';
    if (isEmptyInventory) {
      note = 'This department has no devices in inventory yet.';
      variantClass = ' dept-assignment-banner--inventory';
    } else if (isNoAssignment) {
      note = 'No devices in this department are assigned to you.';
      variantClass = ' dept-assignment-banner--empty-assignment';
    }

    return (
      <div
        className={`dept-assignment-banner${variantClass}`}
        role="note"
        aria-label="Department assignment context"
      >
        <span className="dept-assignment-banner__icon" aria-hidden="true">🏢</span>
        <div className="dept-assignment-banner__content">
          <div className="dept-assignment-banner__summary">
            <span className="dept-assignment-banner__label">Department</span>
            <ul className="dept-assignment-banner__chips">
              {renderDeptChip(
                dept,
                isEmptyInventory ? 'inventory-empty' : isNoAssignment ? 'empty' : ''
              )}
            </ul>
            {note && (
              <span
                className={`dept-assignment-banner__note${isEmptyInventory ? ' dept-assignment-banner__note--inventory' : ''}`}
              >
                {note}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- 2+ departments: expandable categorization ---
  const { assignedToYou, notAssignedToYou, emptyDepartments, withInventory } = groups;
  const hasSplit =
    emptyDepartments.length > 0 ||
    (showUserAssignment && notAssignedToYou.length > 0);

  return (
    <div className="dept-assignment-banner" role="note" aria-label="Department assignment context">
      <span className="dept-assignment-banner__icon" aria-hidden="true">🏢</span>
      <div className="dept-assignment-banner__content">
        <div className="dept-assignment-banner__summary">
          <button
            type="button"
            className="dept-assignment-banner__trigger"
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            <span className="dept-assignment-banner__label">Multiple Departments</span>
            <span className="dept-assignment-banner__count">({departments.length})</span>
            <span className="dept-assignment-banner__chevron" aria-hidden="true">
              {expanded ? '▾' : '▸'}
            </span>
          </button>
          {showUserAssignment && notAssignedToYou.length > 0 && (
            <span className="dept-assignment-banner__note">
              {notAssignedToYou.length} with no devices assigned to you
            </span>
          )}
          {emptyDepartments.length > 0 && (
            <span className="dept-assignment-banner__note dept-assignment-banner__note--inventory">
              {emptyDepartments.length} department{emptyDepartments.length !== 1 ? 's' : ''} with no devices
            </span>
          )}
        </div>
        {expanded && (
          <div className="dept-assignment-banner__details">
            {hasSplit ? (
              <>
                {showUserAssignment
                  ? renderGroup('Devices assigned to you', assignedToYou)
                  : renderGroup('Departments with devices', withInventory)}
                {showUserAssignment && renderGroup('No devices assigned to you', notAssignedToYou, 'empty')}
                {renderGroup('Departments with no devices', emptyDepartments, 'inventory-empty')}
              </>
            ) : (
              <ul className="dept-assignment-banner__chips">
                {departments.map((dept) => renderDeptChip(dept))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentAssignmentBanner;
